/**
 * NW Buy Box V1/V2 — variant selection, dog tabs, subscription selling plans, pricing.
 */
(function () {
  'use strict';

  const PurchaseMode = {
    ONE_TIME: 'one-time',
    SUBSCRIPTION: 'subscription',
  };

  const SEL = {
    root: '[data-nw-buy-box-root]',
    config: 'script[data-nw-buy-box-config]',
    purchaseInput: 'input[name^="nw-bb-purchase-"]',
    variantRadio: '.nw-buy-box__variant-input',
    variantRow: '[data-variant-row]',
    dogTab: '[data-dog-tab]',
    dogPanel: '[data-dog-panel]',
    priceCurrent: '[data-nw-price-current]',
    priceCompare: '[data-nw-price-compare]',
    discountStatic: '[data-nw-discount-static]',
    oneTimeLink: '[data-nw-one-time-link]',
    oneTimePrice: '[data-nw-one-time-price]',
    variantBenefits: '[data-nw-variant-benefits]',
    variantLabel: '.nw-buy-box__variant-label',
    popularBanner: '[data-nw-popular-banner]',
    frequencySection: '[data-nw-frequency-section]',
    frequencySelect: '[data-nw-frequency-select]',
    incentiveRow: '[data-nw-incentive-row]',
    frequencyBenefits: '[data-nw-frequency-benefits]',
  };

  const Utils = {
    formatMoney(cents, moneyFormat) {
      if (cents == null || Number.isNaN(Number(cents))) return '';
      const format = moneyFormat || '${{amount}}';
      const n = Math.max(0, Math.round(Number(cents)));
      const value = (n / 100).toFixed(2);
      const valueNoDecimals = String(Math.max(0, Math.round(n / 100)));
      return format
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/gi, valueNoDecimals)
        .replace(/\{\{\s*amount\s*\}\}/gi, value);
    },

    calcDiscountLabel(compareCents, priceCents) {
      if (!compareCents || compareCents <= priceCents) return '';
      const pct = Math.round(((compareCents - priceCents) / compareCents) * 100);
      return pct > 0 ? `(${pct}% off)` : '';
    },

    inferMonthIntervalFromVariantTitle(title) {
      if (!title || typeof title !== 'string') return null;
      const t = title.toLowerCase();
      const week = t.match(/(\d+)\s*week/);
      if (week) {
        const w = parseInt(week[1], 10);
        if (w === 2) return 1;
        if (w === 4) return 1;
        if (w === 8) return 2;
        if (w === 12) return 3;
        if (w === 16) return 4;
      }
      if (/\bsingle\b/.test(t)) return 1;
      if (/\btriple\b/.test(t)) return 3;
      if (/\bsix\b/.test(t)) return 6;
      if (/\bnine\b/.test(t)) return 9;
      return null;
    },

    extractMonthsIntervalFromPlanName(planName) {
      if (!planName || typeof planName !== 'string') return null;
      const s = planName.trim().toLowerCase();
      if (/\bmonthly\b|\bevery\s+month\b/.test(s)) return 1;
      const digit = s.match(/\b(\d{1,2})\s*[–\-/]?\s*(?:months?|mos\b)/);
      if (digit) return parseInt(digit[1], 10);
      const week = s.match(/(\d+)\s*week/);
      if (week) return Math.max(1, Math.ceil(parseInt(week[1], 10) / 4));
      return null;
    },

    findSellingPlanIndexForMonths(plans, targetMonths) {
      if (!plans?.length || targetMonths == null || Number.isNaN(Number(targetMonths))) return 0;
      const target = Number(targetMonths);
      for (let i = 0; i < plans.length; i++) {
        const inferred = Utils.extractMonthsIntervalFromPlanName(plans[i].name);
        if (inferred === target) return i;
      }
      for (let i = 0; i < plans.length; i++) {
        const wordRe = new RegExp(
          `(?:^|[^0-9])${target}\\s*[–\\-/.]?\\s*(?:months?|weeks?|mos\\b)`,
          'i'
        );
        if (wordRe.test(plans[i].name)) return i;
      }
      return 0;
    },

    findAddToCartForm(root) {
      const productInfo = root.closest('product-info');
      const container = root.closest('.product__info-container') || productInfo;
      if (!container) return null;
      const productForm = container.querySelector('product-form.product-form');
      if (!productForm) return null;
      return (
        productForm.querySelector('form[data-type="add-to-cart-form"]') ||
        productForm.querySelector('form[action*="cart/add"]') ||
        null
      );
    },

    ensureSellingPlanInput(form) {
      let el = form.querySelector('input[name="selling_plan"]');
      if (!el) {
        el = document.createElement('input');
        el.type = 'hidden';
        el.name = 'selling_plan';
        form.appendChild(el);
      }
      return el;
    },

    /**
     * Add one-time line item (no selling_plan). Third-party drawers typically hook this endpoint.
     * @param {string|number} variantId
     * @param {number} [quantity]
     * @returns {Promise<object>}
     */
    addOneTimeToCart(variantId, quantity = 1) {
      const id = Number(variantId);
      const qty = Math.max(1, Number(quantity) || 1);
      if (!id || Number.isNaN(id)) {
        return Promise.reject(new Error('Invalid variant id'));
      }

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ id, quantity: qty }),
      })
        .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
        .then(({ ok, data }) => {
          if (!ok || data.status) {
            const msg =
              data.description || data.message || 'Could not add to cart';
            throw new Error(msg);
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'nw-buy-box-one-time',
              productVariantId: String(id),
              cartData: data,
            });
          }
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:refresh', { bubbles: true, detail: { variantId: id } })
          );
          return data;
        });
    },

    parseConfig(root) {
      const sc = root.querySelector(SEL.config);
      if (!sc?.textContent.trim()) return null;
      try {
        return JSON.parse(sc.textContent);
      } catch (e) {
        console.warn('[NwBuyBox] Invalid config JSON', e);
        return null;
      }
    },

    variantInPanel(cfg, variantId, panelKey) {
      const v = cfg.variants[String(variantId)];
      if (!v || panelKey === 'all') return true;
      if (cfg.useDogSizeMetafield) {
        if (panelKey === 'small') return v.dogSize === cfg.dogSizeSmall;
        if (panelKey === 'medium') return v.dogSize === cfg.dogSizeMedium;
        if (panelKey === 'large') return v.dogSize === cfg.dogSizeLarge;
        return false;
      }
      const title = (v.title || '').toLowerCase();
      const largeMarker = cfg.largeMarkerLower || '';
      const mediumMarker = cfg.mediumMarkerLower || '';
      if (panelKey === 'large') return Boolean(largeMarker) && title.includes(largeMarker);
      if (panelKey === 'medium') return Boolean(mediumMarker) && title.includes(mediumMarker);
      if (panelKey === 'small') {
        const hasLarge = Boolean(largeMarker) && title.includes(largeMarker);
        const hasMedium = Boolean(mediumMarker) && title.includes(mediumMarker);
        return !hasLarge && !hasMedium;
      }
      return false;
    },

    resolvePanelForVariant(cfg, variantId) {
      if (Utils.variantInPanel(cfg, variantId, 'large')) return 'large';
      if (Utils.variantInPanel(cfg, variantId, 'medium')) return 'medium';
      return 'small';
    },

    selectRadioBySlot(radios, slot1Based) {
      if (!radios?.length) return null;
      const n = Number(slot1Based);
      const slot = Number.isFinite(n) && n >= 1 ? n : 1;
      const idx = Math.min(slot, radios.length) - 1;
      return radios[idx] || null;
    },

    parseOrderLines(raw) {
      if (!raw || typeof raw !== 'string') return [];
      return raw
        .split(/\r?\n/)
        .map((line) => line.trim().toLowerCase())
        .filter(Boolean);
    },

    titleSortIndex(title, orderList, panelKey, cfg) {
      const key = (title || '').trim().toLowerCase();
      let idx = orderList.indexOf(key);
      if (idx !== -1) return idx;

      if (panelKey === 'large' && cfg?.largeMarkerLower) {
        idx = orderList.indexOf(`${key} ${cfg.largeMarkerLower}`);
        if (idx !== -1) return idx;
      }
      if (panelKey === 'medium' && cfg?.mediumMarkerLower) {
        idx = orderList.indexOf(`${key} ${cfg.mediumMarkerLower}`);
        if (idx !== -1) return idx;
      }

      for (let i = 0; i < orderList.length; i += 1) {
        const line = orderList[i];
        if (panelKey === 'large' && cfg?.largeMarkerLower) {
          if (key === `${line} ${cfg.largeMarkerLower}`) return i;
        }
        if (panelKey === 'medium' && cfg?.mediumMarkerLower) {
          if (key === `${line} ${cfg.mediumMarkerLower}`) return i;
        }
      }

      return 9999;
    },

    /**
     * Accepts numeric id or Shopify GID (gid://shopify/SellingPlan/123).
     * @param {string|number} raw
     * @returns {string}
     */
    normalizeSellingPlanId(raw) {
      if (raw == null || raw === '') return '';
      const s = String(raw).trim();
      const gid = s.match(/SellingPlan\/(\d+)/i);
      if (gid) return gid[1];
      return s;
    },

    /**
     * @param {object} variant
     * @param {string} overrideId
     * @returns {object|null}
     */
    findPlanByOverride(variant, overrideId) {
      if (!variant?.selling_plans?.length || !overrideId) return null;
      const norm = Utils.normalizeSellingPlanId(overrideId);
      if (!norm) return null;
      return (
        variant.selling_plans.find((p) => String(p.id) === String(norm)) || null
      );
    },

    /**
     * Auto-pick plan from variant title / plan names when no override.
     * @param {object} variant
     * @param {string} [titleHint]
     * @returns {object|null}
     */
    inferPlanForVariant(variant, titleHint) {
      if (!variant?.selling_plans?.length) return null;
      const title = titleHint || variant.title || '';
      const hint = Utils.inferMonthIntervalFromVariantTitle(title);
      let idx = 0;
      if (hint != null) {
        const found = Utils.findSellingPlanIndexForMonths(variant.selling_plans, hint);
        if (found >= 0) idx = found;
      }
      return variant.selling_plans[idx] || variant.selling_plans[0];
    },
  };

  class NwBuyBox {
    constructor(root) {
      this.root = root;
      this._config = null;
      this._form = null;
      this._variantInput = null;
      this._sellingPlanInput = null;
      this._oneTimeOverride = false;
      this._planByVariant = new Map();
      this._originalVariantOrder = null;
      this._frequencySection = null;
      this._frequencySelect = null;
      this._lastFrequencyVariantId = null;

      this._onVariantChange = this._onVariantChange.bind(this);
      this._onPurchaseChange = this._onPurchaseChange.bind(this);
      this._onDogTabClick = this._onDogTabClick.bind(this);
      this._onOneTimeLink = this._onOneTimeLink.bind(this);
      this._onFormVariantChange = this._onFormVariantChange.bind(this);
      this._onFrequencyChange = this._onFrequencyChange.bind(this);
    }

    init() {
      this._config = Utils.parseConfig(this.root);
      if (!this._config?.variants) return;

      this._form = Utils.findAddToCartForm(this.root);
      this._variantInput = this._form?.querySelector('input[name="id"]');

      const otLink = this.root.querySelector(SEL.oneTimeLink);
      if (otLink) otLink.addEventListener('click', this._onOneTimeLink);

      this.root.querySelectorAll(SEL.variantRadio).forEach((inp) => {
        inp.addEventListener('change', this._onVariantChange);
      });

      this.root.querySelectorAll(SEL.dogTab).forEach((btn) => {
        btn.addEventListener('click', this._onDogTabClick);
      });

      this._frequencySection = this.root.querySelector(SEL.frequencySection);
      this._frequencySelect = this.root.querySelector(SEL.frequencySelect);
      if (this._frequencySelect) {
        this._frequencySelect.addEventListener('change', this._onFrequencyChange);
      }

      // Form inputs must exist before purchase/variant UI can dispatch change → _syncAll.
      if (this._form && this._variantInput) {
        this._sellingPlanInput = Utils.ensureSellingPlanInput(this._form);
        this.root.querySelectorAll(SEL.purchaseInput).forEach((inp) => {
          inp.addEventListener('change', this._onPurchaseChange);
        });
        this._variantInput.addEventListener('change', this._onFormVariantChange);
      }

      this._cacheOriginalVariantOrder();
      this._applyPurchaseVariantOrder();
      this._applyPositionBasedUi();

      this._preselectPlansForAllVariants();
      this._syncAll();
    }

    _getPopularLabelText(container) {
      const fromConfig = (this._config?.popularLabel || '').trim();
      if (fromConfig) return fromConfig;
      const existing = container?.querySelector(`${SEL.popularBanner} span`);
      return existing?.textContent?.trim() || '';
    }

    _applyPopularBannerInContainer(container) {
      const labelText = this._getPopularLabelText(container);
      if (!labelText) return;

      const pos = Number(this._config?.popularPosition) || 1;
      const rows = [...container.querySelectorAll(SEL.variantRow)];

      rows.forEach((row) => {
        const labelEl = row.querySelector(SEL.variantLabel);
        row.querySelector(SEL.popularBanner)?.remove();
        labelEl?.classList.remove('nw-buy-box__variant-label--has-popular');
      });

      if (!rows.length) return;

      const idx = Math.min(Math.max(pos, 1), rows.length) - 1;
      const targetRow = rows[idx];
      const labelEl = targetRow?.querySelector(SEL.variantLabel);
      if (!labelEl) return;

      const banner = document.createElement('span');
      banner.className = 'nw-buy-box__popular-banner';
      banner.setAttribute('data-nw-popular-banner', '');
      const inner = document.createElement('span');
      inner.textContent = labelText;
      banner.appendChild(inner);
      labelEl.insertBefore(banner, labelEl.firstChild);
      labelEl.classList.add('nw-buy-box__variant-label--has-popular');
    }

    _applyPreselectedInContainer(container, opts) {
      const radios = container?.querySelectorAll(SEL.variantRadio);
      if (!radios?.length) return null;

      const next = Utils.selectRadioBySlot(
        radios,
        this._config?.pickSlotIndex || 1
      );
      if (!next) return null;

      this.root
        .querySelectorAll(`${SEL.variantRadio}[name="${next.name}"]`)
        .forEach((radio) => {
          radio.checked = false;
        });
      next.checked = true;

      if (opts?.dispatchChange) {
        next.dispatchEvent(new Event('change', { bubbles: true }));
      }

      return next;
    }

    _applyPositionBasedUi(opts) {
      this.root
        .querySelectorAll('[data-nw-variants-panel]')
        .forEach((container) => {
          this._applyPopularBannerInContainer(container);
        });

      const panel = this._getVisiblePanel();
      const container =
        panel?.querySelector('[data-nw-variants-panel]') || panel;
      this._applyPreselectedInContainer(container, opts);
    }

    _usesPurchaseTypeOrders() {
      const cfg = this._config;
      if (!cfg?.customVariantOrderEnabled) return false;
      const layout = cfg.layout || '';
      if (layout !== 'subscription_tabs' && layout !== 'dog_size_subscription') {
        return false;
      }
      return Boolean(
        (cfg.variantOrderOneTime && String(cfg.variantOrderOneTime).trim()) ||
          (cfg.variantOrderSubscription &&
            String(cfg.variantOrderSubscription).trim())
      );
    }

    _getPurchaseOrderList(forcedMode) {
      const cfg = this._config;
      if (!cfg?.customVariantOrderEnabled) return null;
      const mode = forcedMode || this._getPurchaseType();
      const raw =
        mode === PurchaseMode.SUBSCRIPTION
          ? cfg.variantOrderSubscription
          : cfg.variantOrderOneTime;
      const list = Utils.parseOrderLines(raw);
      return list.length ? list : null;
    }

    _cacheOriginalVariantOrder() {
      if (this._originalVariantOrder) return;
      this._originalVariantOrder = new Map();
      this.root
        .querySelectorAll('[data-nw-variants-panel]')
        .forEach((container) => {
          const key =
            container.getAttribute('data-nw-variants-panel') || 'all';
          const ids = [...container.querySelectorAll(SEL.variantRow)].map(
            (row) => row.dataset.variantId
          );
          this._originalVariantOrder.set(key, ids);
        });
    }

    _resetContainerToOriginal(container) {
      const key = container.getAttribute('data-nw-variants-panel') || 'all';
      const ids = this._originalVariantOrder?.get(key);
      if (!ids?.length) return;

      const rowMap = new Map();
      container.querySelectorAll(SEL.variantRow).forEach((row) => {
        rowMap.set(String(row.dataset.variantId), row);
      });

      ids.forEach((id) => {
        const row = rowMap.get(String(id));
        if (row) container.appendChild(row);
      });
    }

    _variantTitleForRow(row) {
      const id = row?.dataset?.variantId;
      const fromConfig = id && this._config?.variants?.[String(id)]?.title;
      if (fromConfig) return fromConfig;
      return row?.querySelector(SEL.variantRadio)?.dataset?.title || '';
    }

    _sortVariantRowsInContainer(container, orderList) {
      if (!container || !orderList?.length) return;
      const panelKey =
        container.getAttribute('data-nw-variants-panel') || 'all';
      const rows = [...container.querySelectorAll(SEL.variantRow)];
      if (rows.length < 2) return;

      rows.sort((rowA, rowB) => {
        const titleA = this._variantTitleForRow(rowA);
        const titleB = this._variantTitleForRow(rowB);
        const idxA = Utils.titleSortIndex(
          titleA,
          orderList,
          panelKey,
          this._config
        );
        const idxB = Utils.titleSortIndex(
          titleB,
          orderList,
          panelKey,
          this._config
        );
        if (idxA !== idxB) return idxA - idxB;
        return 0;
      });

      rows.forEach((row) => container.appendChild(row));
    }

    _applyPurchaseVariantOrder(forcedMode) {
      if (!this._usesPurchaseTypeOrders()) return;

      const orderList = this._getPurchaseOrderList(forcedMode);
      if (!orderList) return;

      this._cacheOriginalVariantOrder();
      this.root
        .querySelectorAll('[data-nw-variants-panel]')
        .forEach((container) => {
          this._resetContainerToOriginal(container);
          this._sortVariantRowsInContainer(container, orderList);
        });

      this._applyPositionBasedUi({ dispatchChange: true });
    }

    _version() {
      return this.root.getAttribute('data-version') || 'v1';
    }

    _getPurchaseType() {
      if (this._version() === 'v2' && !this._oneTimeOverride) {
        return PurchaseMode.SUBSCRIPTION;
      }
      const c = this.root.querySelector(`${SEL.purchaseInput}:checked`);
      if (c) return c.value;
      return this.root.getAttribute('data-purchase-type') || PurchaseMode.ONE_TIME;
    }

    _setPurchaseType(mode) {
      this.root.setAttribute('data-purchase-type', mode);
      const inp = this.root.querySelector(`${SEL.purchaseInput}[value="${mode}"]`);
      if (inp) inp.checked = true;
    }

    _usesSubscriptionPricing() {
      if (this._version() === 'v2') {
        return !this._oneTimeOverride && this._config.hasSellingPlans;
      }
      return (
        this._getPurchaseType() === PurchaseMode.SUBSCRIPTION &&
        this._config.hasSellingPlans
      );
    }

    _getVisiblePanel() {
      const active = this.root.getAttribute('data-tab-active') || 'small';
      if (this.root.getAttribute('data-show-dog-tabs') !== 'true') {
        return this.root.querySelector('[data-nw-variants-panel]') || this.root;
      }
      return this.root.querySelector(`[data-dog-panel="${active}"]:not([hidden])`);
    }

    _getCheckedRadio() {
      const panel = this._getVisiblePanel();
      if (!panel) return this.root.querySelector(`${SEL.variantRadio}:checked`);
      return panel.querySelector(`${SEL.variantRadio}:checked`) ||
        this.root.querySelector(`${SEL.variantRadio}:checked`);
    }

    _getSelectedVariantId() {
      const c = this._getCheckedRadio();
      return c ? c.value : null;
    }

    _getVariantData(id) {
      return this._config.variants[String(id)] || null;
    }

    _hasPlans(id) {
      const v = this._getVariantData(id);
      return Boolean(v?.selling_plans?.length);
    }

    _resolvePlanForVariant(variantId) {
      const v = this._getVariantData(variantId);
      if (!v?.selling_plans?.length) return null;

      const radio = this.root.querySelector(`${SEL.variantRadio}[value="${variantId}"]`);
      const titleHint = radio?.getAttribute('data-title') || v.title || '';

      if (this._config.customSellingPlanEnabled && v.sellingPlanOverride) {
        const custom = Utils.findPlanByOverride(v, v.sellingPlanOverride);
        if (custom) return custom;
        const normId = Utils.normalizeSellingPlanId(v.sellingPlanOverride);
        if (normId) {
          console.warn(
            `[NwBuyBox] Selling plan ${normId} not found on variant ${variantId}; using auto match.`
          );
        }
      }

      return Utils.inferPlanForVariant(v, titleHint);
    }

    _preselectPlansForAllVariants() {
      Object.keys(this._config.variants).forEach((id) => {
        const plan = this._resolvePlanForVariant(id);
        if (plan) this._planByVariant.set(String(id), plan);
      });
    }

    _getPlanForVariant(variantId) {
      const cached = this._planByVariant.get(String(variantId));
      if (cached) return cached;
      return this._resolvePlanForVariant(variantId);
    }

    _getActivePlan() {
      const vid = this._getSelectedVariantId();
      if (!vid) return null;

      const v = this._getVariantData(vid);
      if (!v?.selling_plans?.length) return null;

      if (
        this._frequencySelect &&
        this._frequencySection &&
        !this._frequencySection.hidden &&
        this._usesSubscriptionPricing()
      ) {
        const id = this._frequencySelect.value;
        const fromSelect = v.selling_plans.find((p) => String(p.id) === String(id));
        if (fromSelect) return fromSelect;
      }

      return this._getPlanForVariant(vid);
    }

    _getRecommendedPlanIndex(variant, titleHint) {
      if (!variant?.selling_plans?.length) return 0;

      let recommendedIndex = 0;
      const monthHint = Utils.inferMonthIntervalFromVariantTitle(titleHint || '');
      if (monthHint != null) {
        const idx = Utils.findSellingPlanIndexForMonths(
          variant.selling_plans,
          monthHint
        );
        if (idx >= 0) recommendedIndex = idx;
      }

      if (this._config.customSellingPlanEnabled && variant.sellingPlanOverride) {
        const norm = Utils.normalizeSellingPlanId(variant.sellingPlanOverride);
        const overrideIdx = variant.selling_plans.findIndex(
          (p) => String(p.id) === String(norm)
        );
        if (overrideIdx >= 0) recommendedIndex = overrideIdx;
      }

      return recommendedIndex;
    }

    _rebuildFrequencyOptions(variantId) {
      if (!this._frequencySelect || !variantId) return;

      const v = this._getVariantData(variantId);
      this._frequencySelect.innerHTML = '';
      if (!v?.selling_plans?.length) return;

      const radio = this.root.querySelector(
        `${SEL.variantRadio}[value="${variantId}"]`
      );
      const titleHint = radio?.getAttribute('data-title') || v.title || '';
      const recommendedIndex = this._getRecommendedPlanIndex(v, titleHint);

      v.selling_plans.forEach((plan, i) => {
        const opt = document.createElement('option');
        opt.value = String(plan.id);
        const rec = i === recommendedIndex ? ' (recommended)' : '';
        opt.textContent = `${plan.name}${rec}`;
        this._frequencySelect.appendChild(opt);
      });

      this._frequencySelect.selectedIndex = recommendedIndex;
      this._planByVariant.set(
        String(variantId),
        v.selling_plans[recommendedIndex]
      );
      this._lastFrequencyVariantId = String(variantId);
    }

    _updateFrequencyVisibility() {
      if (!this._frequencySection) return;
      const vid = this._getSelectedVariantId();
      const show =
        this._config.hasSellingPlans &&
        this._usesSubscriptionPricing() &&
        this._hasPlans(vid);
      this._frequencySection.hidden = !show;
      this._frequencySection.setAttribute('aria-hidden', show ? 'false' : 'true');
    }

    _getFrequencyBenefitsHtml(variantId) {
      const v = variantId ? this._getVariantData(variantId) : null;
      const variantHtml = (v?.subscriptionBenefits || '').trim();
      if (variantHtml) return variantHtml;
      return (this._config.defaultFrequencyBenefits || '').trim();
    }

    _updateFrequencyBenefits(variantId) {
      const el = this.root.querySelector(SEL.frequencyBenefits);
      if (!el) return;

      const html = this._getFrequencyBenefitsHtml(variantId);
      if (html) {
        el.innerHTML = html;
        el.hidden = false;
      } else {
        el.innerHTML = '';
        el.hidden = true;
      }
    }

    _updateFrequencyUi(variantId) {
      this._updateFrequencyVisibility();
      this._updateFrequencyBenefits(variantId);
      if (!this._frequencySection || this._frequencySection.hidden || !variantId) {
        return;
      }
      if (String(this._lastFrequencyVariantId) !== String(variantId)) {
        this._rebuildFrequencyOptions(variantId);
      }
    }

    _onFrequencyChange() {
      const vid = this._getSelectedVariantId();
      if (!vid || !this._frequencySelect) return;
      const v = this._getVariantData(vid);
      const plan = v?.selling_plans?.find(
        (p) => String(p.id) === String(this._frequencySelect.value)
      );
      if (plan) {
        this._planByVariant.set(String(vid), plan);
      }
      this._updatePrices();
      this._updateForm();
    }

    async _onOneTimeLink(e) {
      e.preventDefault();
      const btn = e.currentTarget;
      if (btn.getAttribute('aria-busy') === 'true') return;

      const selectedId = this._getSelectedVariantId();
      if (this._variantInput && selectedId) {
        this._variantInput.value = selectedId;
      }

      const variantId =
        (this._variantInput && this._variantInput.value) || selectedId;
      if (!variantId) {
        console.warn('[NwBuyBox] No variant id for one-time add');
        return;
      }

      const qtyInput = this._form?.querySelector('input[name="quantity"]');
      const quantity = qtyInput
        ? Math.max(1, parseInt(qtyInput.value, 10) || 1)
        : 1;

      btn.setAttribute('aria-busy', 'true');
      btn.classList.add('is-loading');

      try {
        await Utils.addOneTimeToCart(variantId, quantity);
      } catch (err) {
        console.error('[NwBuyBox] One-time add failed', err);
      } finally {
        btn.removeAttribute('aria-busy');
        btn.classList.remove('is-loading');
      }
    }

    _onVariantChange() {
      if (this._version() === 'v2') {
        this._oneTimeOverride = false;
        this.root.classList.remove('is-one-time-mode');
        this._setPurchaseType(PurchaseMode.SUBSCRIPTION);
      }
      this._lastFrequencyVariantId = null;
      this._maybeAlignDogTab();
      this._syncAll();
    }

    _onPurchaseChange(e) {
      this._oneTimeOverride = false;
      this.root.classList.remove('is-one-time-mode');

      const clickedMode = e?.currentTarget?.value;
      const checked = this.root.querySelector(`${SEL.purchaseInput}:checked`);
      const activeMode = clickedMode || checked?.value;

      if (activeMode) {
        this.root.setAttribute('data-purchase-type', activeMode);
      }

      if (activeMode) {
        this._lastFrequencyVariantId = null;
        this._applyPurchaseVariantOrder(activeMode);
      } else {
        this._applyPositionBasedUi({ dispatchChange: true });
      }

      const vid = this._getSelectedVariantId();
      if (
        activeMode === PurchaseMode.SUBSCRIPTION &&
        vid &&
        !this._hasPlans(vid)
      ) {
        this._setPurchaseType(PurchaseMode.ONE_TIME);
        this._applyPurchaseVariantOrder(PurchaseMode.ONE_TIME);
      }

      this._syncAll();
    }

    _onDogTabClick(e) {
      const tab = e.currentTarget?.dataset.dogTab;
      if (!tab) return;
      this._activateDogTab(tab, { applySlotDefault: true });
    }

    _activateDogTab(tab, opts) {
      if (this.root.getAttribute('data-show-dog-tabs') !== 'true') return;

      this.root.querySelectorAll(SEL.dogTab).forEach((btn) => {
        const on = btn.dataset.dogTab === tab;
        btn.setAttribute('aria-selected', on ? 'true' : 'false');
        btn.setAttribute('tabindex', on ? '0' : '-1');
        btn.classList.toggle('is-active', on);
      });

      this.root.querySelectorAll(SEL.dogPanel).forEach((panel) => {
        const on = panel.dataset.dogPanel === tab;
        panel.hidden = !on;
      });

      this.root.setAttribute('data-tab-active', tab);

      if (opts?.applySlotDefault) {
        this._applyPositionBasedUi({ dispatchChange: true });
        this._syncAll();
        return;
      }
      this._syncAll();
    }

    _maybeAlignDogTab() {
      const checked = this._getCheckedRadio();
      if (!checked || this.root.getAttribute('data-show-dog-tabs') !== 'true') return;
      const grp = Utils.resolvePanelForVariant(this._config, checked.value);
      this._activateDogTab(grp, { applySlotDefault: false });
    }

    _onFormVariantChange() {
      const vid = this._variantInput.value;
      if (!vid) return;
      let radio = null;
      this.root.querySelectorAll(SEL.variantRadio).forEach((inp) => {
        if (String(inp.value) === String(vid)) radio = inp;
      });
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        this._maybeAlignDogTab();
        this._syncAll();
      }
    }

    _priceStateForVariant(v, mode, isSelected) {
      const useSub = mode === PurchaseMode.SUBSCRIPTION && v.selling_plans?.length;
      let current = v.price;
      let compare = v.compare_at_price;

      if (useSub) {
        const plan = isSelected
          ? this._getActivePlan()
          : this._getPlanForVariant(v.id);
        if (plan) {
          current = plan.price;
          compare = plan.compare_at_price || v.compare_at_price;
        }
      }

      let discountLabel = v.discountTag || '';
      if (!discountLabel) {
        discountLabel = Utils.calcDiscountLabel(compare, current);
      }

      return { current, compare, discountLabel };
    }

    _updatePrices() {
      const mode = this._usesSubscriptionPricing()
        ? PurchaseMode.SUBSCRIPTION
        : PurchaseMode.ONE_TIME;
      const selectedId = this._getSelectedVariantId();
      const mf = this._config.moneyFormat;

      this.root.querySelectorAll(SEL.variantRow).forEach((row) => {
        const id = row.dataset.variantId;
        const v = this._getVariantData(id);
        if (!v) return;

        const isSelected = id === selectedId;
        const { current, compare, discountLabel } = this._priceStateForVariant(
          v,
          mode,
          isSelected
        );

        const curEl = row.querySelector(SEL.priceCurrent);
        const cmpEl = row.querySelector(SEL.priceCompare);
        const discEl = row.querySelector(SEL.discountStatic);

        if (curEl) curEl.textContent = Utils.formatMoney(current, mf);
        if (cmpEl) {
          if (compare && compare > current) {
            cmpEl.textContent = Utils.formatMoney(compare, mf);
            cmpEl.hidden = false;
          } else {
            cmpEl.textContent = '';
            cmpEl.hidden = true;
          }
        }
        if (discEl) {
          if (discountLabel) {
            discEl.textContent = discountLabel;
            discEl.hidden = false;
          } else {
            discEl.textContent = '';
            discEl.hidden = true;
          }
        }
      });

      const otPrice = this.root.querySelector(SEL.oneTimePrice);
      if (otPrice && selectedId) {
        const v = this._getVariantData(selectedId);
        if (v) otPrice.textContent = Utils.formatMoney(v.price, mf);
      }
    }

    _updateBenefitsPanels() {
      if (this._version() !== 'v2') return;
      const selectedId = this._getSelectedVariantId();
      const featuredOnly = this._config?.benefitsFeaturedOnly !== false;

      this.root.querySelectorAll(SEL.variantBenefits).forEach((panel) => {
        const row = panel.closest(SEL.variantRow);
        const expanded = featuredOnly
          ? true
          : row?.dataset.variantId === selectedId;
        panel.classList.toggle('is-collapsed', !expanded);
      });
    }

    _applySelectionClasses() {
      const vid = this._getSelectedVariantId();
      this.root.querySelectorAll(SEL.variantRow).forEach((row) => {
        row.classList.toggle('is-selected', row.dataset.variantId === vid);
      });
    }

    _setSubscriptionPurchaseDisabled(disabled) {
      const sub = this.root.querySelector(`${SEL.purchaseInput}[value="subscription"]`);
      if (!sub) return;
      sub.disabled = disabled;
      sub.parentElement?.classList.toggle('is-disabled', disabled);
    }

    _updateForm() {
      const vid = this._getSelectedVariantId();
      if (!vid) return;

      if (this._variantInput && String(this._variantInput.value) !== String(vid)) {
        this._variantInput.value = vid;
        this._variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (!this._sellingPlanInput) return;

      const useSub = this._usesSubscriptionPricing() && this._hasPlans(vid);
      if (useSub) {
        const plan = this._getActivePlan();
        const planId = plan ? String(plan.id) : '';
        this._sellingPlanInput.disabled = false;
        this._sellingPlanInput.value = planId;
      } else {
        this._sellingPlanInput.value = '';
        this._sellingPlanInput.disabled = true;
      }
    }

    _syncAll() {
      const vid = this._getSelectedVariantId();
      if (vid && !this._hasPlans(vid)) {
        this._setSubscriptionPurchaseDisabled(true);
        if (this._getPurchaseType() === PurchaseMode.SUBSCRIPTION) {
          this._setPurchaseType(PurchaseMode.ONE_TIME);
        }
      } else {
        this._setSubscriptionPurchaseDisabled(false);
      }

      this._updateFrequencyUi(vid);
      this._updatePrices();
      this._updateBenefitsPanels();
      this._applySelectionClasses();
      this._updateForm();
    }
  }

  function bootstrap() {
    document.querySelectorAll(SEL.root).forEach((root) => {
      try {
        const box = new NwBuyBox(root);
        box.init();
        Object.defineProperty(root, '_nwBuyBox', { value: box, enumerable: false, configurable: true });
      } catch (err) {
        console.error('[NwBuyBox] init failed', err);
      }
    });
  }

  window.NwBuyBox = NwBuyBox;
  window.NwBuyBoxBootstrap = bootstrap;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();